import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import useHttp from './useHttp';
import AuthContext from '../context/authContext';

const POLL_INTERVAL_MS = 5000;
const POLL_TIMEOUT_MS = 20 * 60 * 1000;

const useStatisticJob = ({
  statisticType,
  endpoint,
  filterParams,
  autoFetchOnMount = true,
}) => {
  const authCtx = useContext(AuthContext);
  const { sendRequest } = useHttp();

  const [payload, setPayload] = useState(null);
  const [isStale, setIsStale] = useState(false);
  const [cacheComputedAt, setCacheComputedAt] = useState(null);
  const [cacheExpiresAt, setCacheExpiresAt] = useState(null);
  const [status, setStatus] = useState('idle');
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);

  const filterParamsKey = useMemo(
    () => (filterParams ? JSON.stringify(filterParams) : ''),
    [filterParams]
  );

  const activeRequestRef = useRef(0);
  const pollTimeoutRef = useRef(null);
  const pollDeadlineRef = useRef(0);
  const refetchRef = useRef(null);

  const clearPolling = () => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  };

  const pollJobStatus = useCallback(
    async jobId => {
      if (!jobId) return;
      try {
        const res = await sendRequest({
          url: `/api/v1/statistic/jobs/${jobId}`,
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
        });
        if (res && res.job) {
          setJob(res.job);
          if (res.job.status === 'done') {
            clearPolling();
            if (refetchRef.current) await refetchRef.current();
            return;
          }
          if (res.job.status === 'failed' || res.job.status === 'cancelled') {
            clearPolling();
            setStatus('failed');
            setError(res.job.errorMessage || 'Berechnung fehlgeschlagen.');
            return;
          }
          setStatus(res.job.status === 'running' ? 'running' : 'pending');
        }
      } catch (_err) {
        // ignore transient polling failures
      }

      if (Date.now() < pollDeadlineRef.current) {
        pollTimeoutRef.current = setTimeout(() => pollJobStatus(jobId), POLL_INTERVAL_MS);
      }
    },
    [authCtx, sendRequest]
  );

  const refetchAfterJobDone = useCallback(async () => {
    const myRequest = ++activeRequestRef.current;
    try {
      const res = await sendRequest({
        url: endpoint,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
        params: filterParams,
      });
      if (activeRequestRef.current !== myRequest) return;
      if (res && res.status === 'fresh') {
        setPayload(res.payload);
        setIsStale(false);
        setCacheComputedAt(res.cacheComputedAt || null);
        setCacheExpiresAt(res.cacheExpiresAt || null);
        setStatus('fresh');
        setError(null);
      }
    } catch (err) {
      if (activeRequestRef.current !== myRequest) return;
      setError(err?.message || 'Laden der aktualisierten Daten fehlgeschlagen.');
    }
  }, [authCtx, endpoint, filterParams, sendRequest]);

  refetchRef.current = refetchAfterJobDone;

  const fetch = useCallback(async (options = {}) => {
    if (!endpoint || !filterParams) return;
    clearPolling();
    const myRequest = ++activeRequestRef.current;
    setStatus('fetching');
    setError(null);

    const requestParams = options.forceRecompute
      ? { ...filterParams, forceRecompute: 1 }
      : filterParams;

    try {
      const res = await sendRequest({
        url: endpoint,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
        params: requestParams,
      });
      if (activeRequestRef.current !== myRequest) return;

      if (res && res.status === 'fresh') {
        setPayload(res.payload);
        setIsStale(false);
        setCacheComputedAt(res.cacheComputedAt || null);
        setCacheExpiresAt(res.cacheExpiresAt || null);
        setJob(null);
        setStatus('fresh');
        return;
      }

      if (res && (res.status === 'stale' || res.status === 'pending')) {
        const treatAsPending = !!options.forceRecompute && res.status === 'stale';
        setPayload(res.payload || null);
        setIsStale(res.status === 'stale' && !treatAsPending);
        setCacheComputedAt(res.cacheComputedAt || null);
        setCacheExpiresAt(res.cacheExpiresAt || null);
        setJob(res.job || null);
        setStatus(treatAsPending ? 'pending' : res.status === 'stale' ? 'stale' : 'pending');

        if (res.job && res.job._id) {
          pollDeadlineRef.current = Date.now() + POLL_TIMEOUT_MS;
          pollTimeoutRef.current = setTimeout(
            () => pollJobStatus(res.job._id),
            POLL_INTERVAL_MS
          );
        }
        return;
      }
    } catch (err) {
      if (activeRequestRef.current !== myRequest) return;
      setStatus('failed');
      setError(err?.message || 'Anfrage fehlgeschlagen.');
    }
  }, [authCtx, endpoint, filterParams, pollJobStatus, sendRequest]);

  const loadCacheEntry = useCallback(
    async entryId => {
      if (!entryId) return;
      clearPolling();
      const myRequest = ++activeRequestRef.current;
      setStatus('fetching');
      setError(null);
      try {
        const res = await sendRequest({
          url: `/api/v1/statistic/cache-entries/${entryId}/payload`,
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
        });
        if (activeRequestRef.current !== myRequest) return;
        if (res) {
          setPayload(res.payload);
          setIsStale(!!res.isExpired);
          setCacheComputedAt(res.computedAt || null);
          setCacheExpiresAt(res.expiresAt || null);
          setJob(null);
          setStatus(res.isExpired ? 'stale' : 'fresh');
        }
      } catch (err) {
        if (activeRequestRef.current !== myRequest) return;
        setStatus('failed');
        setError(err?.message || 'Laden des Cache-Eintrags fehlgeschlagen.');
      }
    },
    [authCtx, sendRequest]
  );

  useEffect(() => {
    if (autoFetchOnMount) fetch();
    return () => {
      clearPolling();
    };
  }, [filterParamsKey]);

  useEffect(() => {
    const onJobDone = evt => {
      const detail = evt && evt.detail;
      if (!detail) return;
      if (job && detail._id && String(detail._id) === String(job._id)) {
        refetchAfterJobDone();
        return;
      }
      if (detail.statisticType === statisticType && filterParams) {
        const matches =
          detail.filterParams &&
          detail.filterParams.startDate === filterParams.startDate &&
          detail.filterParams.endDate === filterParams.endDate &&
          (detail.filterParams.branchScope || 'all') === (filterParams.branchScope || 'all');
        if (matches) refetchAfterJobDone();
      }
    };

    const onJobFailed = evt => {
      const detail = evt && evt.detail;
      if (!detail) return;
      if (job && detail._id && String(detail._id) === String(job._id)) {
        clearPolling();
        setJob(detail);
        setStatus('failed');
        setError(detail.errorMessage || 'Berechnung fehlgeschlagen.');
      }
    };

    const onJobProgress = evt => {
      const detail = evt && evt.detail;
      if (!detail) return;
      if (job && detail._id && String(detail._id) === String(job._id)) {
        setJob(detail);
        if (detail.status === 'running') setStatus('running');
      }
    };

    window.addEventListener('statistic-job-done', onJobDone);
    window.addEventListener('statistic-job-failed', onJobFailed);
    window.addEventListener('statistic-job-progress', onJobProgress);
    return () => {
      window.removeEventListener('statistic-job-done', onJobDone);
      window.removeEventListener('statistic-job-failed', onJobFailed);
      window.removeEventListener('statistic-job-progress', onJobProgress);
    };
  }, [job, statisticType, filterParamsKey, refetchAfterJobDone]);

  const refresh = useCallback(() => fetch({ forceRecompute: true }), [fetch]);

  // Shared rendering flags. These make it possible for every statistic
  // view (and the shared StatisticViewShell) to use *one* consistent rule
  // for "should I show a skeleton or the payload". Without this each view
  // rolled its own `isFetching` / `isFetchingStats` and briefly rendered
  // "Keine Daten" in transitional states (e.g. status === 'idle' right
  // after mount, before fetch() had a chance to flip status to 'fetching').
  const isLoading =
    status === 'idle' ||
    status === 'fetching' ||
    status === 'pending' ||
    status === 'running';
  const hasPayload = payload != null;
  // True as soon as there is something meaningful to render: either fresh
  // data, a stale cache while a new compute is in flight, or a failed
  // final state (views still render whatever payload exists + the error
  // banner; absence of payload on failure is handled by the shell).
  const isReady = hasPayload || status === 'failed';

  return {
    payload,
    isStale,
    cacheComputedAt,
    cacheExpiresAt,
    status,
    job,
    error,
    fetch,
    refresh,
    loadCacheEntry,
    isLoading,
    hasPayload,
    isReady,
  };
};

export default useStatisticJob;
